// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Persistent (IndexedDB) second-tier cache for the parsed normalized-artifact
// envelope ({ points, engineLayout, parserWarnings, schemaVersion, parserId,
// ... }) produced by parseFlightEntryEnvelope() in database flight-source mode.
// The in-memory parsedFlightEntryCache Map in app.js stays as the synchronous
// tier-1; this layer survives page reloads / app relaunches so the trends page
// (and the dashboard) do not re-fetch the normalized artifact over HTTP and
// re-parse its JSON on every visit. For a twin like N2000AX each artifact is a
// large JSON payload, so this is the difference between a cold-feeling reload
// and an instant one.
//
// Keyed by the immutable normalizedArtifactId: a re-ingest mints a brand-new
// artifact id, so a stale envelope can never be served — superseded entries
// simply age out of the LRU. That makes invalidation a non-issue; there is no
// version handshake against the server.
//
// Caching is strictly an optimization: every entry point degrades silently to
// null/false on any failure (no IndexedDB, private browsing, serialization
// error, quota exceeded, ...), so parsing always falls back to the network and
// flight loading never breaks. Mirrors the addEventListener-based IndexedDB
// pattern in analysis-cache-store.js.

const ARTIFACT_CACHE_DB_NAME = "aeroInsightNormalizedArtifactCache";
const ARTIFACT_CACHE_STORE_NAME = "parsedArtifactEnvelopes";
const ARTIFACT_CACHE_MAX_ENTRIES = 24;
// Bump when the stored envelope shape changes (a new field in
// cloneParsedFlightEnvelope, a points schema change, ...) so records written by
// an older build are ignored instead of deserialized into the wrong shape.
// v2: envelopes carry comboAlignment/auxSeriesKeys for CGR combos — a v1 record
// written by an older build would silently disable the unaligned-combo gating.
// v3: refresh stored engine metadata, retained sources, and combo offsets.
// v4: retain per-source timing for separate CGR engine recordings.
const ARTIFACT_CACHE_FORMAT_VERSION = 4;

// Permanently false for the session if an envelope ever fails structured clone,
// so we stop paying the clone cost and stop logging DataCloneError on every
// write.
let cloneSupported = true;
// The explicit clone self-test only needs to pass once per session.
let cloneProven = false;

function openArtifactCacheStore() {
  if (!globalThis.indexedDB) return Promise.resolve(null);
  return new Promise((resolve) => {
    let request;
    try {
      request = indexedDB.open(ARTIFACT_CACHE_DB_NAME, 1);
    } catch {
      resolve(null);
      return;
    }
    request.addEventListener("upgradeneeded", () => {
      const store = request.result.createObjectStore(ARTIFACT_CACHE_STORE_NAME, { keyPath: "key" });
      store.createIndex("lastAccess", "lastAccess");
    });
    request.addEventListener("success", () => resolve(request.result));
    request.addEventListener("error", () => resolve(null));
    request.addEventListener("blocked", () => resolve(null));
  });
}

async function withArtifactCacheStore(mode, callback) {
  const database = await openArtifactCacheStore();
  if (!database) return null;
  return new Promise((resolve) => {
    let transaction;
    try {
      transaction = database.transaction(ARTIFACT_CACHE_STORE_NAME, mode);
    } catch {
      database.close();
      resolve(null);
      return;
    }
    const store = transaction.objectStore(ARTIFACT_CACHE_STORE_NAME);
    const done = (value) => {
      database.close();
      resolve(value);
    };
    transaction.addEventListener("error", () => done(null));
    transaction.addEventListener("abort", () => done(null));
    callback(store, done);
  });
}

// Fire-and-forget LRU bump on read; failures are ignored.
function touchLastAccess(key) {
  withArtifactCacheStore("readwrite", (store, done) => {
    const request = store.get(key);
    request.addEventListener("success", () => {
      const record = request.result;
      if (record) {
        record.lastAccess = Date.now();
        store.put(record);
      }
      done(true);
    });
    request.addEventListener("error", () => done(null));
  }).catch(() => {});
}

export async function readCachedNormalizedArtifactEnvelope(key) {
  if (!key) return null;
  const record = await withArtifactCacheStore("readonly", (store, done) => {
    const request = store.get(key);
    request.addEventListener("success", () => done(request.result ?? null));
    request.addEventListener("error", () => done(null));
  }).catch(() => null);
  if (!record || record.formatVersion !== ARTIFACT_CACHE_FORMAT_VERSION) return null;
  const envelope = record.envelope;
  if (!envelope || typeof envelope !== "object" || !Array.isArray(envelope.points)) return null;
  touchLastAccess(key);
  return envelope;
}

// Evict oldest entries (by lastAccess) until at most MAX remain. Runs inside the
// same readwrite transaction as the preceding put.
function pruneToMaxEntries(store, done) {
  const countRequest = store.count();
  countRequest.addEventListener("success", () => {
    const overflow = countRequest.result - ARTIFACT_CACHE_MAX_ENTRIES;
    if (overflow <= 0) {
      done(true);
      return;
    }
    let removed = 0;
    const cursorRequest = store.index("lastAccess").openCursor();
    cursorRequest.addEventListener("success", () => {
      const cursor = cursorRequest.result;
      if (!cursor || removed >= overflow) {
        done(true);
        return;
      }
      cursor.delete();
      removed += 1;
      cursor.continue();
    });
    cursorRequest.addEventListener("error", () => done(null));
  });
  countRequest.addEventListener("error", () => done(null));
}

export async function writeCachedNormalizedArtifactEnvelope(key, flightId, envelope) {
  if (!key || !envelope || typeof envelope !== "object" || !Array.isArray(envelope.points)) return false;
  if (!cloneSupported) return false;
  // Prove (once) the envelope survives structured clone before persisting; this
  // converts a would-be DataCloneError on store.put into a one-time, silent
  // opt-out for the session.
  if (!cloneProven) {
    try {
      if (typeof structuredClone === "function") structuredClone(envelope);
      cloneProven = true;
    } catch {
      cloneSupported = false;
      return false;
    }
  }
  const record = {
    key,
    flightId: flightId || "",
    envelope,
    formatVersion: ARTIFACT_CACHE_FORMAT_VERSION,
    createdAt: new Date().toISOString(),
    lastAccess: Date.now(),
  };
  const saved = await withArtifactCacheStore("readwrite", (store, done) => {
    let putRequest;
    try {
      putRequest = store.put(record);
    } catch {
      // Synchronous DataCloneError (defensive; the self-test above should have
      // already caught a non-cloneable envelope).
      cloneSupported = false;
      done(null);
      return;
    }
    putRequest.addEventListener("success", () => pruneToMaxEntries(store, done));
    putRequest.addEventListener("error", () => done(null));
  }).catch(() => null);
  return Boolean(saved);
}
